import {
  memo,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type UIEvent,
} from 'react'
import {
  applyEffectsToActions,
  BEAT_COLOR_DEFAULT,
  BEAT_COLOR_SWATCHES,
  BEAT_PROP_LABEL,
  BEAT_SIZE_BUTTON,
  BEAT_SIZE_CHIP,
  BEAT_SIZE_DEFAULT,
  BEAT_SIZE_OPTIONS,
  beatEffectTargets,
  beatPropsForObject,
  defaultBeatRadiusM,
  effectSummaryLabel,
  effectsFromActions,
  radiusEffectChip,
  targetLabel,
  type BeatEffect,
  type BeatEffectProp,
} from './beatEffects'
import { formatDistanceM } from './format'
import { iconSvg } from './icons'
import { DRAW_COLORS, presetLabel, zoneCfgFor, zoneLabel } from './toolUi'
import type { DisplayUnits, ObjectSize, ScenarioObject, TimelineAction } from './types'

export type ReviewRunChipItem = {
  id: string
  /** Per-version display number on the chip. */
  localNum: number
}

/**
 * Run-number chips for the run-options modal.
 * Memoized so parent clock ticks (elapsedMs) do not rebuild the buttons — that was
 * delaying CSS :hover by hundreds of ms while Instant Replay / Preview was running.
 */
export const ReviewRunChipRow = memo(function ReviewRunChipRow({
  chips,
  activeRunId,
  canPick,
  onPick,
}: {
  chips: readonly ReviewRunChipItem[]
  activeRunId: string | null
  canPick: boolean
  onPick: (runId: string) => void
}) {
  return (
    <div
      className="sc-run-options-runs-row"
      role="group"
      aria-labelledby="sc-run-options-runs-label"
    >
      {chips.map((chip) => {
        const active = activeRunId === chip.id
        return (
          <button
            key={chip.id}
            type="button"
            className={`sc-review-run-chip${active ? ' is-active' : ''}`}
            title={`Open Run ${chip.localNum}`}
            aria-label={`Open Run ${chip.localNum}`}
            aria-pressed={active}
            disabled={!canPick}
            onClick={() => {
              if (!canPick) return
              onPick(chip.id)
            }}
          >
            <span className="sc-review-run-chip-num">{chip.localNum}</span>
          </button>
        )
      })}
    </div>
  )
})

const CHAT_STICK_PX = 56

/**
 * Scroll region that stays pinned to the bottom while the user has not scrolled away.
 * Focus on the composer should call `scrollToBottom` so the entry box sits on the latest line.
 */
export function ChatThreadScroll({
  className,
  deps,
  children,
  scrollRef,
}: {
  className?: string
  /** When these change and the view is still pinned, scroll to bottom. */
  deps: unknown
  children: ReactNode
  /** Optional external ref (e.g. so compose focus can jump to bottom). */
  scrollRef?: MutableRefObject<HTMLDivElement | null>
}) {
  const innerRef = useRef<HTMLDivElement | null>(null)
  const pinnedRef = useRef(true)

  function setNode(node: HTMLDivElement | null): void {
    innerRef.current = node
    if (scrollRef) scrollRef.current = node
  }

  function onScroll(e: UIEvent<HTMLDivElement>): void {
    const el = e.currentTarget
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight
    pinnedRef.current = gap <= CHAT_STICK_PX
  }

  useEffect(() => {
    const el = innerRef.current
    if (!el || !pinnedRef.current) return
    el.scrollTop = el.scrollHeight
  }, [deps])

  return (
    <div ref={setNode} className={className} onScroll={onScroll}>
      {children}
    </div>
  )
}

export function scrollChatThreadToBottom(
  el: HTMLDivElement | null | undefined,
): void {
  if (!el) return
  el.scrollTop = el.scrollHeight
}

/** Pane label band. Optional back caret kept for rare callers; spine “List” is the usual return. */
/** Soft caps so Timeline edit cards stay scannable. */
export const NARRATION_TITLE_MAX = 80
export const NARRATION_SUMMARY_MAX = 280

/**
 * Title + description for a Timeline beat (edit mode).
 * Local draft so keystrokes do not commitSession / Fields on every character.
 */
export function NarrationBeatCopyFields({
  eventId,
  title,
  summary,
  summaryPlaceholder,
  autoFocus,
  onCommitTitle,
  onCommitSummary,
  onFocus,
}: {
  eventId: string
  title: string
  summary: string
  /** Shown when summary is empty (e.g. sync object template) — not written until the user edits. */
  summaryPlaceholder?: string
  autoFocus?: boolean
  onCommitTitle: (title: string) => void
  onCommitSummary: (summary: string) => void
  onFocus?: () => void
}) {
  const [titleDraft, setTitleDraft] = useState(title)
  const [summaryDraft, setSummaryDraft] = useState(summary)
  const [titleFlash, setTitleFlash] = useState(false)
  const [summaryFlash, setSummaryFlash] = useState(false)
  const titleFocus = useRef(title)
  const summaryFocus = useRef(summary)
  const titleFlashTimer = useRef<number | null>(null)
  const summaryFlashTimer = useRef<number | null>(null)

  useEffect(() => {
    setTitleDraft(title)
    titleFocus.current = title
  }, [eventId, title])

  useEffect(() => {
    setSummaryDraft(summary)
    summaryFocus.current = summary
  }, [eventId, summary])

  useEffect(
    () => () => {
      if (titleFlashTimer.current != null) window.clearTimeout(titleFlashTimer.current)
      if (summaryFlashTimer.current != null) window.clearTimeout(summaryFlashTimer.current)
    },
    []
  )

  function flashTitle(): void {
    if (titleFlashTimer.current != null) window.clearTimeout(titleFlashTimer.current)
    setTitleFlash(true)
    titleFlashTimer.current = window.setTimeout(() => {
      setTitleFlash(false)
      titleFlashTimer.current = null
    }, 650)
  }

  function flashSummary(): void {
    if (summaryFlashTimer.current != null) window.clearTimeout(summaryFlashTimer.current)
    setSummaryFlash(true)
    summaryFlashTimer.current = window.setTimeout(() => {
      setSummaryFlash(false)
      summaryFlashTimer.current = null
    }, 650)
  }

  return (
    <>
      <input
        className={`sc-fin sc-tl-narration-title${titleFlash ? ' sc-field-flash' : ''}`}
        value={titleDraft}
        placeholder="Title"
        aria-label="Story card title"
        maxLength={NARRATION_TITLE_MAX}
        autoFocus={autoFocus}
        onChange={(e) => setTitleDraft(e.target.value.slice(0, NARRATION_TITLE_MAX))}
        onFocus={() => {
          titleFocus.current = titleDraft
          onFocus?.()
        }}
        onBlur={() => {
          const next = titleDraft.trim() || 'Story card'
          setTitleDraft(next)
          if (next !== titleFocus.current) {
            onCommitTitle(next)
            flashTitle()
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        }}
      />
      <textarea
        className={`sc-fin sc-tl-narration-summary${summaryFlash ? ' sc-field-flash' : ''}`}
        value={summaryDraft}
        placeholder={summaryPlaceholder || 'Description'}
        aria-label="Story card description"
        maxLength={NARRATION_SUMMARY_MAX}
        rows={2}
        onChange={(e) => setSummaryDraft(e.target.value.slice(0, NARRATION_SUMMARY_MAX))}
        onFocus={() => {
          summaryFocus.current = summaryDraft
          onFocus?.()
        }}
        onBlur={() => {
          if (summaryDraft !== summaryFocus.current) {
            onCommitSummary(summaryDraft)
            flashSummary()
          }
        }}
      />
    </>
  )
}

type BeatEffectDraft = {
  mode: 'add' | 'edit'
  index?: number
  objectId: string
  prop: BeatEffectProp | ''
  color: string
  size: ObjectSize
  radiusM: number
}

function draftFromEffect(
  mode: 'add' | 'edit',
  effect: BeatEffect,
  objects: readonly ScenarioObject[],
  index?: number
): BeatEffectDraft {
  const obj = objects.find((o) => o.id === effect.objectId)
  return {
    mode,
    index,
    objectId: effect.objectId,
    prop: effect.prop,
    color: effect.prop === 'color' ? effect.color : BEAT_COLOR_DEFAULT,
    size: effect.prop === 'size' ? effect.size : BEAT_SIZE_DEFAULT,
    radiusM:
      effect.prop === 'radius'
        ? effect.radiusM
        : obj
          ? defaultBeatRadiusM(obj)
          : 100,
  }
}

function effectFromDraft(draft: BeatEffectDraft): BeatEffect | null {
  const objectId = draft.objectId.trim()
  if (!objectId || !draft.prop) return null
  if (draft.prop === 'color') {
    const color = draft.color.trim()
    if (!color) return null
    return { prop: 'color', objectId, color }
  }
  if (draft.prop === 'size') {
    return { prop: 'size', objectId, size: draft.size }
  }
  if (!(draft.radiusM > 0) || !Number.isFinite(draft.radiusM)) return null
  return { prop: 'radius', objectId, radiusM: draft.radiusM }
}

/**
 * Beat Effects: color / size / radius `setParams` on targetable board objects.
 * Compact summary rows + optional draft sheet — not a wall of pickers.
 *
 * Draft flow: target → property (when more than one applies) → value → Done.
 * Prop list follows Inspect (what the object can actually change).
 */
export function BeatEffectsEditor({
  eventId,
  actions,
  objects,
  preferredObjectId,
  displayUnits = 'imperial',
  disabled,
  onCommitActions,
  onFocus,
}: {
  eventId: string
  actions: readonly TimelineAction[]
  objects: readonly ScenarioObject[]
  /** Prefill target when adding (e.g. map selection). */
  preferredObjectId?: string | null
  displayUnits?: DisplayUnits
  disabled?: boolean
  onCommitActions: (actions: TimelineAction[]) => void
  onFocus?: () => void
}) {
  const targets = beatEffectTargets(objects)
  const [rows, setRows] = useState<BeatEffect[]>(() => effectsFromActions(actions))
  const [draft, setDraft] = useState<BeatEffectDraft | null>(null)

  useEffect(() => {
    setRows(effectsFromActions(actions))
    setDraft(null)
  }, [eventId, actions])

  function commit(next: BeatEffect[]): void {
    setRows(next)
    onCommitActions(applyEffectsToActions(actions, next))
  }

  function preferredTargetId(): string {
    if (preferredObjectId && targets.some((t) => t.id === preferredObjectId)) {
      return preferredObjectId
    }
    return ''
  }

  function objectFor(objectId: string): ScenarioObject | undefined {
    return targets.find((t) => t.id === objectId) ?? objects.find((o) => o.id === objectId)
  }

  function propsForTarget(objectId: string): BeatEffectProp[] {
    const obj = objectFor(objectId)
    return obj ? beatPropsForObject(obj) : []
  }

  function withTarget(objectId: string, prev: BeatEffectDraft): BeatEffectDraft {
    const obj = objectFor(objectId)
    const props = propsForTarget(objectId)
    const prop =
      prev.prop && props.includes(prev.prop)
        ? prev.prop
        : props.length === 1
          ? props[0]!
          : ''
    return {
      ...prev,
      objectId,
      prop,
      radiusM: obj ? defaultBeatRadiusM(obj) : prev.radiusM,
    }
  }

  function openAdd(): void {
    if (!targets.length || disabled) return
    const objectId = preferredTargetId()
    const base: BeatEffectDraft = {
      mode: 'add',
      objectId: '',
      prop: '',
      color: BEAT_COLOR_DEFAULT,
      size: BEAT_SIZE_DEFAULT,
      radiusM: 100,
    }
    setDraft(objectId ? withTarget(objectId, base) : base)
    onFocus?.()
  }

  function openEdit(index: number): void {
    if (disabled) return
    const row = rows[index]
    if (!row) return
    setDraft(draftFromEffect('edit', row, objects, index))
    onFocus?.()
  }

  function saveDraft(): void {
    if (!draft) return
    const next = effectFromDraft(draft)
    if (!next) return
    // One row per object+prop — replace an existing twin.
    const base =
      draft.mode === 'edit' && draft.index != null
        ? rows.filter((_, i) => i !== draft.index)
        : rows
    const withoutTwin = base.filter(
      (r) => !(r.objectId === next.objectId && r.prop === next.prop)
    )
    commit([...withoutTwin, next])
    setDraft(null)
  }

  if (!targets.length && rows.length === 0 && !draft) {
    return null
  }

  const drafting = draft != null
  const draftTargetSelected = Boolean(draft?.objectId.trim())
  const draftObj = draftTargetSelected && draft ? objectFor(draft.objectId) : undefined
  const draftProps = draftObj ? beatPropsForObject(draftObj) : []
  const draftPropSelected = Boolean(draft?.prop && draftProps.includes(draft.prop))
  const showPropPicker = draftTargetSelected && draftProps.length > 1
  const draftReady = draft != null && effectFromDraft(draft) != null
  const draftZone = draftObj ? zoneCfgFor(draftObj.type) : undefined

  return (
    <div className="sc-tl-effects" onFocus={onFocus}>
      {rows.map((row, idx) => {
        if (draft?.mode === 'edit' && draft.index === idx) return null
        return (
          <div key={`${eventId}-fx-${idx}`} className="sc-tl-effect-summary">
            <button
              type="button"
              className="sc-tl-effect-summary-main"
              disabled={disabled || drafting}
              title="Edit effect"
              aria-label={`Edit ${effectSummaryLabel(row, objects)}`}
              onClick={() => openEdit(idx)}
            >
              <span className="sc-tl-effect-value" aria-hidden="true">
                {row.prop === 'color' ? (
                  <span
                    className="sc-tl-effect-swatch"
                    style={{ background: row.color }}
                  />
                ) : row.prop === 'size' ? (
                  <span className="sc-tl-effect-size-chip">
                    {BEAT_SIZE_CHIP[row.size]}
                  </span>
                ) : (
                  <span className="sc-tl-effect-size-chip sc-tl-effect-radius-chip">
                    {radiusEffectChip(row.radiusM, displayUnits)}
                  </span>
                )}
              </span>
              <span className="sc-tl-effect-summary-text">
                {effectSummaryLabel(row, objects)}
              </span>
            </button>
            <button
              type="button"
              className="sc-tl-effect-remove"
              title="Remove effect"
              aria-label="Remove effect"
              disabled={disabled || drafting}
              onClick={() => commit(rows.filter((_, i) => i !== idx))}
            >
              ×
            </button>
          </div>
        )
      })}

      {draft ? (
        <div className="sc-tl-effect-draft">
          <label className="sc-tl-effect-field">
            <span className="sc-tl-effect-field-lbl">Target Object</span>
            <select
              className="sc-fin sc-tl-effect-target"
              aria-label="Target Object"
              disabled={disabled || !targets.length}
              value={draft.objectId}
              onChange={(e) => setDraft(withTarget(e.target.value, draft))}
            >
              <option value="">Select object…</option>
              {!targets.some((t) => t.id === draft.objectId) && draft.objectId ? (
                <option value={draft.objectId}>Missing object</option>
              ) : null}
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {targetLabel(t)}
                </option>
              ))}
            </select>
          </label>

          {showPropPicker ? (
            <label className="sc-tl-effect-field">
              <span className="sc-tl-effect-field-lbl">Effect</span>
              <select
                className="sc-fin sc-tl-effect-target"
                aria-label="Effect"
                disabled={disabled}
                value={draft.prop}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    prop: (e.target.value || '') as BeatEffectProp | '',
                  })
                }
              >
                <option value="">Select effect…</option>
                {draftProps.map((p) => (
                  <option key={p} value={p}>
                    {BEAT_PROP_LABEL[p]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {draftPropSelected && draft.prop === 'color' ? (
            <div className="sc-tl-effect-field">
              <span className="sc-tl-effect-field-lbl">{BEAT_PROP_LABEL.color}</span>
              <SwatchRow
                colors={BEAT_COLOR_SWATCHES}
                value={draft.color}
                disabled={disabled}
                onPick={(color) => setDraft({ ...draft, color })}
              />
            </div>
          ) : null}

          {draftPropSelected && draft.prop === 'size' ? (
            <div className="sc-tl-effect-field">
              <span className="sc-tl-effect-field-lbl">{BEAT_PROP_LABEL.size}</span>
              <div className="sc-seg sc-tl-effect-size-seg">
                {BEAT_SIZE_OPTIONS.map((sz) => (
                  <button
                    key={sz}
                    type="button"
                    className={draft.size === sz ? 'on' : ''}
                    disabled={disabled}
                    onClick={() => setDraft({ ...draft, size: sz })}
                  >
                    {BEAT_SIZE_BUTTON[sz]}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {draftPropSelected && draft.prop === 'radius' && draftObj ? (
            <div className="sc-tl-effect-field">
              <div className="sc-tl-effect-radius-head">
                <span className="sc-tl-effect-field-lbl">
                  {zoneLabel(draftObj.type)}
                </span>
                <span className="sc-tl-effect-radius-val">
                  {formatDistanceM(
                    draftZone
                      ? Math.min(
                          draftZone.max,
                          Math.max(draftZone.min, draft.radiusM)
                        )
                      : draft.radiusM,
                    displayUnits
                  )}
                </span>
              </div>
              {draftZone ? (
                <>
                  <input
                    type="range"
                    min={draftZone.min}
                    max={draftZone.max}
                    step={draftZone.step}
                    value={Math.min(
                      draftZone.max,
                      Math.max(draftZone.min, draft.radiusM)
                    )}
                    disabled={disabled}
                    aria-valuetext={formatDistanceM(
                      Math.min(
                        draftZone.max,
                        Math.max(draftZone.min, draft.radiusM)
                      ),
                      displayUnits
                    )}
                    onChange={(e) =>
                      setDraft({ ...draft, radiusM: Number(e.target.value) })
                    }
                  />
                  <div className="sc-presets sc-tl-effect-radius-presets">
                    {draftZone.presets.map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`sc-btn tiny${
                          Math.abs(draft.radiusM - m) < 0.5 ? ' on' : ''
                        }`}
                        disabled={disabled}
                        onClick={() => setDraft({ ...draft, radiusM: m })}
                      >
                        {presetLabel(m, displayUnits)}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="sc-hint">
                  {radiusEffectChip(draft.radiusM, displayUnits)}
                </p>
              )}
            </div>
          ) : null}

          <div className="sc-tl-effect-draft-actions">
            <button
              type="button"
              className="sc-tl-effect-draft-cancel"
              disabled={disabled}
              onClick={() => setDraft(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sc-tl-effect-draft-done"
              disabled={disabled || !draftReady}
              onClick={saveDraft}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}

      {!disabled && targets.length > 0 && !drafting ? (
        <button type="button" className="sc-tl-effects-add" onClick={openAdd}>
          <span className="sc-tl-effects-add-plus" aria-hidden="true">
            +
          </span>
          <span>Add effect</span>
        </button>
      ) : null}
    </div>
  )
}

export function PaneEyebrow({
  label,
  onBack,
  backTitle,
  onEggClick,
  trailing,
}: {
  label: string
  onBack?: () => void
  backTitle?: string
  /** Timeline easter-egg: clicking the label toggles eggs (not a back control). */
  onEggClick?: () => void
  /** Absolute right — keeps the eyebrow band / underline unchanged (like the back caret). */
  trailing?: ReactNode
}) {
  return (
    <div className="sc-pane-eyebrow">
      {onBack && backTitle ? (
        <button
          type="button"
          className="sc-pane-eyebrow-back"
          aria-label={backTitle}
          title={backTitle}
          onClick={onBack}
        >
          <svg
            className="sc-pane-eyebrow-back-ico"
            viewBox="0 0 24 24"
            width="15"
            height="15"
            aria-hidden="true"
          >
            <path
              d="M20 12H7.8M12.2 6.5 7 12l5.2 5.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
      {onEggClick ? (
        <button type="button" className="sc-pane-eyebrow-egg" onClick={onEggClick}>
          {label}
        </button>
      ) : (
        <span className="sc-pane-eyebrow-label">{label}</span>
      )}
      {trailing ? <div className="sc-pane-eyebrow-trailing">{trailing}</div> : null}
    </div>
  )
}

export function PencilIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  )
}

/** Delete / dismiss — same 13×13 box as Pencil/Pdf so card tools stay optically aligned. */
export function CloseIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  )
}

export function CopyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="9" y="9" width="11" height="12" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </svg>
  )
}

export function EmailLine({
  email,
  link = true,
  onActivate,
}: {
  email: string
  link?: boolean
  /** When set (e.g. people cards), clicking the address selects — copy still stops propagation. */
  onActivate?: (ev: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }) => void
}) {
  const [copied, setCopied] = useState(false)
  const addr = email.trim()
  if (!addr) return null
  return (
    <div
      className="sc-email-line"
      onClick={
        onActivate
          ? (e) => {
              onActivate(e)
            }
          : undefined
      }
    >
      {link ? (
        <a
          className="sc-email-mailto"
          href={`mailto:${addr}`}
          title={`Email ${addr}`}
          onClick={(e) => e.stopPropagation()}
        >
          {addr}
        </a>
      ) : (
        <span className="sc-email-text">{addr}</span>
      )}
      <button
        type="button"
        className={`sc-email-copy${copied ? ' copied' : ''}`}
        title={copied ? 'Copied' : 'Copy email'}
        aria-label={copied ? 'Copied' : `Copy ${addr}`}
        onClick={(e) => {
          e.stopPropagation()
          void navigator.clipboard.writeText(addr).then(() => {
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1200)
          })
        }}
      >
        <CopyIcon />
      </button>
    </div>
  )
}
export function PdfIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  )
}

/** Standard share (iOS-style) — icon for Share / Publish panels. */
export function ShareIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
      <path d="M16 6l-4-4-4 4" />
      <path d="M12 2v14" />
    </svg>
  )
}

function middleEllipsizeLabel(text: string, maxLen = 28): string {
  const t = text.trim()
  if (t.length <= maxLen) return t
  // Prefer keeping " · Planned|Completed|Template" and/or a trailing "(n)" version suffix.
  const lifeMatch = t.match(/(\s·\s(?:Planned|Completed|Template))\s*$/i)
  const life = lifeMatch?.[1] ?? ''
  const withoutLife = life ? t.slice(0, -life.length) : t
  const verMatch = withoutLife.match(/(\s*\(\d+\))\s*$/)
  const ver = verMatch?.[1] ?? ''
  const body = (ver ? withoutLife.slice(0, -verMatch![0].length) : withoutLife).trimEnd()
  const ellipsis = '…'
  const trySuffix = (suffix: string): string | null => {
    const budget = maxLen - suffix.length - ellipsis.length
    if (budget < 8) return null
    return `${body.slice(0, budget).trimEnd()}${ellipsis}${suffix}`
  }
  // Prefer version number when space is tight (that's what distinguishes runs).
  return (
    trySuffix(`${ver}${life}`) ??
    trySuffix(ver) ??
    trySuffix(life.trimStart()) ??
    `${body.slice(0, Math.max(4, maxLen - 1)).trimEnd()}${ellipsis}`
  )
}

export function StripPicker({
  value,
  groups,
  onChange,
  variant = 'strip',
}: {
  value: string
  groups: { label: string; items: { value: string; label: string; closedLabel?: string }[] }[]
  onChange: (value: string) => void
  /** strip: wide clock-bar button. title: "…" on the rail title. */
  variant?: 'strip' | 'title'
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const titleMenu = variant === 'title'
  const currentItem = groups.flatMap((g) => g.items).find((item) => item.value === value)
  const currentFull = currentItem?.closedLabel ?? currentItem?.label ?? 'Session'
  const current = middleEllipsizeLabel(currentFull, 30)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!groups.some((g) => g.items.length > 0)) return null

  return (
    <div className={titleMenu ? 'sc-title-menu-wrap' : 'sc-strip-pick-wrap'} ref={wrapRef}>
      <button
        type="button"
        className={titleMenu ? 'sc-icon-btn' : `sc-strip-pick${open ? ' open' : ''}`}
        aria-label="Sessions"
        aria-expanded={open}
        title={titleMenu ? 'Sessions' : currentFull}
        onClick={() => setOpen((v) => !v)}
      >
        {titleMenu ? '…' : current}
      </button>
      {open ? (
        <div className={`sc-strip-menu${titleMenu ? ' sc-title-menu' : ''}`} role="listbox" aria-label="Sessions">
          {groups.map((group) => (
            <div key={group.label} className="sc-strip-menu-group">
              <div className="sc-strip-menu-lab">{group.label}</div>
              {group.items.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  role="option"
                  aria-selected={item.value === value}
                  className={item.value === value ? 'on' : ''}
                  title={item.label}
                  onClick={() => {
                    onChange(item.value)
                    setOpen(false)
                  }}
                >
                  {middleEllipsizeLabel(item.label, 42)}
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
export function ToolIcon({
  typeId,
  color,
  plain = false,
}: {
  typeId: string
  color: string
  plain?: boolean
}) {
  return (
    <span
      className="sc-badge"
      style={plain ? { color } : { background: `${color}22`, color }}
      dangerouslySetInnerHTML={{ __html: iconSvg(typeId) }}
    />
  )
}

/** Shared style controls for Line and Freehand Sketch — placement and later edit. */
export function StrokeFields({
  label,
  color,
  width,
  opacity,
  disabled,
  labelInputId,
  onLabel,
  onColor,
  onWidth,
  onOpacity,
}: {
  label: string
  color: string
  width: number
  opacity: number
  disabled?: boolean
  labelInputId: string
  onLabel: (value: string) => void
  onColor: (color: string) => void
  onWidth: (px: number) => void
  onOpacity: (opacity: number) => void
}) {
  return (
    <>
      <div className="sc-field">
        <label className="sc-flbl" htmlFor={labelInputId}>
          Label
        </label>
        <input
          id={labelInputId}
          className="sc-fin"
          value={label}
          disabled={disabled}
          placeholder="e.g. Hose lines"
          onChange={(e) => onLabel(e.target.value)}
        />
      </div>
      <div className="sc-field">
        <span className="sc-flbl">Color</span>
        <SwatchRow colors={DRAW_COLORS} value={color} disabled={disabled} onPick={onColor} />
      </div>
      <div className="sc-field">
        <label className="sc-flbl">Size - {width}px</label>
        <input
          type="range"
          min={1}
          max={20}
          value={width}
          disabled={disabled}
          onChange={(e) => onWidth(Number(e.target.value))}
        />
      </div>
      <div className="sc-field">
        <label className="sc-flbl">Opacity - {Math.round(opacity * 100)}%</label>
        <input
          type="range"
          min={10}
          max={100}
          step={10}
          value={Math.round(opacity * 100)}
          disabled={disabled}
          onChange={(e) => onOpacity(Number(e.target.value) / 100)}
        />
      </div>
    </>
  )
}

export function SwatchRow({
  colors,
  value,
  disabled,
  onPick,
}: {
  colors: readonly string[]
  value: string
  disabled?: boolean
  onPick: (color: string) => void
}) {
  return (
    <div className="sc-swatches">
      {colors.map((color) => (
        <button
          key={color}
          type="button"
          className={`sc-swatch${value.toLowerCase() === color.toLowerCase() ? ' on' : ''}`}
          style={{ background: color }}
          disabled={disabled}
          aria-label={color}
          onClick={() => onPick(color)}
        />
      ))}
    </div>
  )
}

/**
 * Session chat composer — local draft state so keystrokes do not re-render ScenariosPage.
 * Enter sends; Shift+Enter inserts a newline. No separate Send button.
 */
export function RoomChatCompose({
  onSend,
  onFocus,
  placeholder = 'Write a message…',
}: {
  onSend: (body: string) => void
  onFocus?: () => void
  placeholder?: string
}) {
  const [draft, setDraft] = useState('')
  function submit(): void {
    const text = draft.trim()
    if (!text) return
    onSend(text)
    setDraft('')
  }
  return (
    <div className="sc-comments-form sc-chat-compose">
      <textarea
        className="sc-fin"
        placeholder={placeholder}
        value={draft}
        rows={2}
        aria-label={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => onFocus?.()}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
          e.preventDefault()
          submit()
        }}
      />
    </div>
  )
}
